/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { cn } from "@/lib/utils";
import {
  Users,
  User as UserIcon,
  Shield,
  Globe,
  CalendarDays,
  Swords,
  Radio,
  Trophy,
  Target,
  Layers3,
  Gift,
  BookOpen,
  Gamepad2,
  Flag,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Activity,
  Clock,
  CheckCircle2,
  Flame,
  DollarSign,
  BarChart3,
  MessageSquareWarning,
  Megaphone,
  Dot,
  Smartphone,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface GameweekRow {
  id: number;
  name: string;
  phase: string;
  roundNumber: number;
  deadline: string;
  status: "UPCOMING" | "OPEN" | "LOCKED" | "COMPLETED";
  _count?: { gameweekTeams: number };
}

interface LeaderboardStats {
  totalTeams: number;
  averagePoints: number;
  highestPoints: number;
  lowestPoints: number;
  topTeam: { name: string; owner: string; points: number } | null;
}

interface LfsGame {
  id: number;
  title: string;
  isOpen: boolean;
  isActive: boolean;
  isFinished: boolean;
  _count: { participants: number; rounds: number };
}

interface WeekStatus {
  days: { date: string; challenge: { title: string } | null }[];
  missingCount: number;
}

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalAdmins: number;
  content: {
    players: number;
    clubs: number;
    nations: number;
    matches: number;
    liveMatches: number;
    stories: number;
    activeAnnouncements: number;
    dailyChallenges: number;
    activeLfsGames: number;
    pendingReports: number;
  };
  engagement: {
    dcCompletionsToday: number;
    lfsParticipants: number;
  };
  // Platform breakdown — see backend admin.service.ts for definitions.
  // "totalApp" / "totalWeb" split on expoPushToken presence.
  // "active*" = distinct users with GameweekTeam edit OR DcCompletion
  // completed in the last 30 days.
  platform?: {
    totalApp: number;
    totalWeb: number;
    activeApp: number;
    activeWeb: number;
    activeTotal: number;
  };
  usersByWeek: { weekStart: string; count: number }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

const statusColor: Record<string, string> = {
  UPCOMING: "bg-gray-100 text-gray-700",
  OPEN: "bg-green-100 text-green-700",
  LOCKED: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-blue-100 text-blue-700",
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  // Pull the handful of admin metrics we need in parallel. Every query has
  // `select` so an error/unknown shape coerces to safe defaults — the
  // dashboard never crashes the whole page over one slow endpoint.
  const { data: gameweeks = [] } = useQuery<GameweekRow[]>({
    queryKey: ["dash", "gameweeks"],
    queryFn: () => apiClient.get(API_ENDPOINTS.GAMEWEEKS.LIST),
    select: (r: any) => (Array.isArray(r) ? r : []),
    staleTime: 30_000,
  });

  const { data: players } = useQuery<{ pagination: { total: number } }>({
    queryKey: ["dash", "players"],
    queryFn: () => apiClient.get(`${API_ENDPOINTS.PLAYERS.LIST}?limit=1`),
    staleTime: 60_000,
  });

  const { data: fantasyStats } = useQuery<LeaderboardStats>({
    queryKey: ["dash", "fantasy-stats"],
    queryFn: () => apiClient.get(API_ENDPOINTS.FANTASYLEADERBOARD.STATS),
    staleTime: 30_000,
  });

  const { data: lfsGames = [] } = useQuery<LfsGame[]>({
    queryKey: ["dash", "lfs"],
    queryFn: () => apiClient.get(API_ENDPOINTS.LFS.GAMES),
    select: (r: any) => (Array.isArray(r) ? r : []),
    staleTime: 30_000,
  });

  const { data: weekStatus } = useQuery<WeekStatus>({
    queryKey: ["dash", "dc-week"],
    queryFn: () => apiClient.get(API_ENDPOINTS.DAILY_CHALLENGE.WEEK_STATUS),
    select: (r: any) => ({
      days: Array.isArray(r?.days) ? r.days : [],
      missingCount:
        typeof r?.missingCount === "number" ? r.missingCount : 0,
    }),
    staleTime: 30_000,
  });

  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ["dash", "stats"],
    queryFn: () => apiClient.get(API_ENDPOINTS.DASHBOARD.STATS),
    staleTime: 30_000,
  });

  // ── Derived ─────────────────────────────────────────────────────────────

  const totalPlayers = stats?.content?.players ?? players?.pagination?.total ?? 0;
  const totalUsers = stats?.totalUsers ?? 0;
  const activeUsers = stats?.activeUsers ?? 0; // last 30 days
  const totalFantasyTeams = fantasyStats?.totalTeams ?? 0;
  const avgFantasyPoints = fantasyStats?.averagePoints ?? 0;

  const usersGrowthData = (stats?.usersByWeek ?? []).map((w) => ({
    // Short label like "Apr 15"
    name: new Date(w.weekStart).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
    users: w.count,
  }));

  const totalSignupsLast12w = usersGrowthData.reduce(
    (sum, w) => sum + w.users,
    0,
  );

  const currentGw =
    gameweeks.find((g) => g.status === "OPEN") ??
    gameweeks.find((g) => g.status === "LOCKED") ??
    gameweeks.find((g) => g.status === "UPCOMING") ??
    null;

  const completedGameweeks = gameweeks.filter((g) => g.status === "COMPLETED");

  const activeLfs = lfsGames.find((g) => !g.isFinished) ?? null;

  // Participation chart — completed gameweeks only, showing team count per gw.
  // Keeps the last 8 for legibility.
  const participationData = completedGameweeks
    .slice(-8)
    .map((g) => ({
      name: g.name.replace("Matchweek ", "MW"),
      teams: g._count?.gameweekTeams ?? 0,
    }));

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-gray-500 mt-1">
            A live snapshot of Ball Knowledge operations.
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <Activity className="h-3.5 w-3.5 text-green-500" />
          <span>Live data</span>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <KpiCard
          label="Total users"
          value={totalUsers.toLocaleString()}
          icon={<Users className="h-5 w-5" />}
          tone="sky"
          href="/users"
          sub={
            activeUsers > 0 ? `+${activeUsers} new last 30d` : "Growth view below"
          }
        />
        <KpiCard
          label="Active users"
          value={(stats?.platform?.activeTotal ?? 0).toLocaleString()}
          icon={<Smartphone className="h-5 w-5" />}
          tone="rose"
          href="/analytics/users"
          sub={
            stats?.platform
              ? `${stats.platform.activeApp.toLocaleString()} app · ${stats.platform.activeWeb.toLocaleString()} web`
              : "last 30d activity"
          }
        />
        <KpiCard
          label="Players"
          value={totalPlayers.toLocaleString()}
          icon={<UserIcon className="h-5 w-5" />}
          tone="blue"
          href="/players"
        />
        <KpiCard
          label="Fantasy teams"
          value={totalFantasyTeams.toLocaleString()}
          icon={<Trophy className="h-5 w-5" />}
          tone="amber"
          href="/leaderboard"
          sub={
            totalFantasyTeams > 0
              ? `Avg ${avgFantasyPoints} pts`
              : undefined
          }
        />
        <KpiCard
          label="Completed GWs"
          value={String(completedGameweeks.length)}
          icon={<CalendarDays className="h-5 w-5" />}
          tone="emerald"
          href="/gameweeks"
          sub={
            currentGw
              ? `${currentGw.name} · ${currentGw.status}`
              : "No active gameweek"
          }
        />
        <KpiCard
          label="LFS participants"
          value={
            activeLfs ? activeLfs._count.participants.toLocaleString() : "0"
          }
          icon={<Shield className="h-5 w-5" />}
          tone="purple"
          href="/lfs"
          sub={
            activeLfs
              ? `${activeLfs._count.rounds} rounds · ${activeLfs.title.slice(0, 32)}`
              : "No active game"
          }
        />
      </div>

      {/* Alerts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {/* Daily Challenge week alert */}
        {weekStatus && weekStatus.missingCount > 0 ? (
          <AlertCard
            tone="amber"
            icon={<AlertTriangle className="h-5 w-5" />}
            title={`${weekStatus.missingCount}/7 days unscheduled`}
            body={`Missing: ${weekStatus.days
              .filter((d) => !d.challenge)
              .map((d) => shortDate(d.date))
              .join(" · ")}`}
            actionLabel="Fix in Daily Challenge"
            actionHref="/daily-challenge"
          />
        ) : weekStatus ? (
          <AlertCard
            tone="emerald"
            icon={<CheckCircle2 className="h-5 w-5" />}
            title="Week fully scheduled"
            body="All 7 upcoming daily challenges are in place."
            actionLabel="View schedule"
            actionHref="/daily-challenge"
          />
        ) : null}

        {/* Current gameweek / deadline alert */}
        {currentGw ? (
          <AlertCard
            tone={
              currentGw.status === "OPEN"
                ? "emerald"
                : currentGw.status === "LOCKED"
                  ? "amber"
                  : "blue"
            }
            icon={<Clock className="h-5 w-5" />}
            title={`${currentGw.name} — ${currentGw.status}`}
            body={`Deadline ${new Date(currentGw.deadline).toLocaleString(
              undefined,
              {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              },
            )}`}
            actionLabel="Manage gameweek"
            actionHref="/gameweeks"
          />
        ) : (
          <AlertCard
            tone="gray"
            icon={<CalendarDays className="h-5 w-5" />}
            title="No active gameweek"
            body="Create a gameweek to let users submit picks and lineups."
            actionLabel="Create gameweek"
            actionHref="/gameweeks"
          />
        )}
      </div>

      {/* Quick Access */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Quick Access</h3>
            <p className="text-sm text-gray-500">
              Every admin tool one click away.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <QuickTile
            href="/matches"
            icon={<Swords className="h-5 w-5" />}
            label="Matches"
            tone="rose"
          />
          <QuickTile
            href="/live-scorer"
            icon={<Radio className="h-5 w-5" />}
            label="Live Scorer"
            tone="red"
          />
          <QuickTile
            href="/gameweeks"
            icon={<CalendarDays className="h-5 w-5" />}
            label="Gameweeks"
            tone="emerald"
          />
          <QuickTile
            href="/players"
            icon={<UserIcon className="h-5 w-5" />}
            label="Players"
            tone="blue"
          />
          <QuickTile
            href="/clubs"
            icon={<Shield className="h-5 w-5" />}
            label="Clubs"
            tone="indigo"
          />
          <QuickTile
            href="/nations"
            icon={<Globe className="h-5 w-5" />}
            label="Nations"
            tone="cyan"
          />
          <QuickTile
            href="/leaderboard"
            icon={<Trophy className="h-5 w-5" />}
            label="Leaderboard"
            tone="amber"
          />
          <QuickTile
            href="/leagues"
            icon={<Layers3 className="h-5 w-5" />}
            label="Leagues"
            tone="violet"
          />
          <QuickTile
            href="/lfs"
            icon={<Shield className="h-5 w-5" />}
            label="Last Fan Standing"
            tone="yellow"
          />
          <QuickTile
            href="/daily-challenge"
            icon={<Target className="h-5 w-5" />}
            label="Daily Challenge"
            tone="orange"
          />
          <QuickTile
            href="/winners"
            icon={<Gift className="h-5 w-5" />}
            label="Winners"
            tone="pink"
          />
          <QuickTile
            href="/phases"
            icon={<Flag className="h-5 w-5" />}
            label="Phases"
            tone="slate"
          />
          <QuickTile
            href="/users"
            icon={<Users className="h-5 w-5" />}
            label="Users"
            tone="sky"
          />
          <QuickTile
            href="/stories"
            icon={<BookOpen className="h-5 w-5" />}
            label="Stories"
            tone="teal"
          />
          <QuickTile
            href="/modes"
            icon={<Gamepad2 className="h-5 w-5" />}
            label="Modes"
            tone="fuchsia"
          />
          <QuickTile
            href="/quiz/types"
            icon={<TrendingUp className="h-5 w-5" />}
            label="Quiz Types"
            tone="lime"
          />
        </div>
      </div>

      {/* User Growth + Ad Revenue row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* User growth — last 12 weeks */}
        <Card className="lg:col-span-2 border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                  User Growth
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  New sign-ups per week · last 12 weeks
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 bg-sky-50 text-sky-700 px-2.5 py-1 rounded-full">
                  <TrendingUp className="h-3 w-3" />
                  <span className="text-xs font-bold">
                    +{totalSignupsLast12w}
                  </span>
                </div>
                <Link
                  to="/users"
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  Users <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            {usersGrowthData.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center gap-2 text-gray-400">
                <Users className="h-8 w-8" />
                <p className="text-sm font-medium">No sign-ups yet</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={usersGrowthData}>
                    <defs>
                      <linearGradient
                        id="userGrowthFill"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#0ea5e9"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="100%"
                          stopColor="#0ea5e9"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      stroke="#9CA3AF"
                      fontSize={11}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#9CA3AF"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="users"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      fill="url(#userGrowthFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ad Revenue — AdMob integration needed */}
        <Card className="border border-gray-200 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                  Ad Revenue
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  AdMob earnings snapshot
                </p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <DollarSign className="h-5 w-5" />
              </div>
            </div>

            <div className="flex flex-col items-center text-center py-4">
              <p className="text-4xl font-extrabold text-gray-900">—</p>
              <p className="text-xs text-gray-500 font-medium mt-1">
                Connect AdMob to see live revenue
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                Integration
              </p>
              <p className="text-xs text-gray-600 leading-relaxed">
                Revenue, impressions, and fill rate are reported by Google
                AdMob — server-side integration required to pull metrics
                here.
              </p>
              <a
                href="https://apps.admob.com/v2/home"
                target="_blank"
                rel="noreferrer"
                className="mt-3 text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1"
              >
                Open AdMob console <ArrowRight className="h-3 w-3" />
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content & engagement snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <MiniStat
          label="Matches"
          value={stats?.content?.matches ?? 0}
          sub={stats?.content?.liveMatches ? `${stats?.content?.liveMatches} live` : undefined}
          icon={<Swords className="h-4 w-4" />}
          tone="rose"
          href="/matches"
          badge={stats?.content?.liveMatches ? "LIVE" : undefined}
        />
        <MiniStat
          label="Clubs"
          value={stats?.content?.clubs ?? 0}
          icon={<Shield className="h-4 w-4" />}
          tone="indigo"
          href="/clubs"
        />
        <MiniStat
          label="Nations"
          value={stats?.content?.nations ?? 0}
          icon={<Globe className="h-4 w-4" />}
          tone="cyan"
          href="/nations"
        />
        <MiniStat
          label="Stories"
          value={stats?.content?.stories ?? 0}
          icon={<BookOpen className="h-4 w-4" />}
          tone="teal"
          href="/stories"
        />
        <MiniStat
          label="Announcements"
          value={stats?.content?.activeAnnouncements ?? 0}
          sub="active"
          icon={<Megaphone className="h-4 w-4" />}
          tone="amber"
          href="/announcements"
        />
        <MiniStat
          label="Pending reports"
          value={stats?.content?.pendingReports ?? 0}
          icon={<MessageSquareWarning className="h-4 w-4" />}
          tone={
            (stats?.content?.pendingReports ?? 0) > 0 ? "red" : "emerald"
          }
          href="/quiz/reports"
          badge={
            (stats?.content?.pendingReports ?? 0) > 0 ? "ACTION" : undefined
          }
        />
      </div>

      {/* Engagement today */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  DC solves today
                </p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">
                  {stats?.engagement.dcCompletionsToday ?? 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {totalUsers > 0
                    ? `${(
                        ((stats?.engagement.dcCompletionsToday ?? 0) /
                          totalUsers) *
                        100
                      ).toFixed(1)}% of users`
                    : "—"}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                <Target className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  LFS participants (all-time)
                </p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">
                  {(stats?.engagement.lfsParticipants ?? 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats?.content?.activeLfsGames ?? 0} active games
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-yellow-50 text-yellow-600 flex items-center justify-center">
                <Flame className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  Total daily challenges
                </p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">
                  {stats?.content?.dailyChallenges ?? 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  all scheduled to date
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <BarChart3 className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Participation chart */}
        <Card className="lg:col-span-2 border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                  Teams per Gameweek
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Participation across the last {participationData.length}{" "}
                  completed gameweeks.
                </p>
              </div>
              <Link
                to="/gameweeks"
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            {participationData.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center gap-2 text-gray-400">
                <CalendarDays className="h-8 w-8" />
                <p className="text-sm font-medium">No completed gameweeks yet</p>
                <p className="text-xs">Data will appear as gameweeks finish.</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={participationData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      stroke="#9CA3AF"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#9CA3AF"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="teams" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard highlight */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                  Fantasy leader
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Top team overall</p>
              </div>
              <Link
                to="/leaderboard"
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                Full board <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            {fantasyStats?.topTeam ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Trophy className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 truncate">
                      {fantasyStats.topTeam.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {fantasyStats.topTeam.owner}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-extrabold text-yellow-600">
                      {fantasyStats.topTeam.points}
                    </p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                      points
                    </p>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-2">
                  <StatBlock
                    label="Highest"
                    value={fantasyStats.highestPoints ?? 0}
                  />
                  <StatBlock
                    label="Lowest"
                    value={fantasyStats.lowestPoints ?? 0}
                  />
                </div>
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center gap-2 text-gray-400">
                <Trophy className="h-8 w-8" />
                <p className="text-sm font-medium">No fantasy teams yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming daily challenges strip */}
      {weekStatus && weekStatus.days.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900">
              Upcoming Daily Challenges
            </h3>
            <Link
              to="/daily-challenge"
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              Manage <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {weekStatus.days.map((d) => {
              const filled = !!d.challenge;
              return (
                <Link
                  key={d.date}
                  to="/daily-challenge"
                  className={cn(
                    "rounded-xl border p-3 hover:shadow-sm transition-all",
                    filled
                      ? "bg-white border-green-200"
                      : "bg-amber-50 border-amber-200 border-dashed",
                  )}
                >
                  <p className="text-[10px] font-bold text-gray-400 uppercase">
                    {shortDate(d.date)}
                  </p>
                  {filled ? (
                    <>
                      <div className="flex items-center gap-1 mt-1 mb-1">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        <span className="text-[10px] font-bold text-green-700 uppercase">
                          Set
                        </span>
                      </div>
                      <p className="text-xs font-semibold text-gray-800 line-clamp-2">
                        {d.challenge!.title}
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center gap-1 mt-1 text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      <span className="text-[10px] font-bold uppercase">
                        Missing
                      </span>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent gameweeks */}
      {gameweeks.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900">Recent Gameweeks</h3>
            <Link
              to="/gameweeks"
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <Card className="border border-gray-200 shadow-sm">
            <div className="grid grid-cols-12 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 px-5 py-2.5 border-b">
              <div className="col-span-4">Name</div>
              <div className="col-span-2">Phase</div>
              <div className="col-span-3">Deadline</div>
              <div className="col-span-1 text-center">Teams</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {gameweeks.slice(0, 5).map((g, i) => (
              <Link
                to="/gameweeks"
                key={g.id}
                className={cn(
                  "grid grid-cols-12 px-5 py-3 items-center text-sm hover:bg-gray-50 transition-colors",
                  i !== Math.min(gameweeks.length, 5) - 1 &&
                    "border-b border-gray-100",
                )}
              >
                <div className="col-span-4 font-medium text-gray-900 truncate">
                  {g.name}
                </div>
                <div className="col-span-2 text-gray-600 text-xs">
                  {g.phase.replace(/_/g, " ")}
                </div>
                <div className="col-span-3 text-gray-500 text-xs">
                  {new Date(g.deadline).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
                <div className="col-span-1 text-center">
                  <Badge className="bg-blue-50 text-blue-700 font-semibold">
                    {g._count?.gameweekTeams ?? 0}
                  </Badge>
                </div>
                <div className="col-span-2 text-right">
                  <span
                    className={cn(
                      "text-[10px] font-bold px-2 py-0.5 rounded-full uppercase",
                      statusColor[g.status] ?? "bg-gray-100 text-gray-700",
                    )}
                  >
                    {g.status}
                  </span>
                </div>
              </Link>
            ))}
          </Card>
        </div>
      )}
    </Layout>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon,
  tone,
  href,
  sub,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: "blue" | "amber" | "emerald" | "purple" | "sky" | "rose";
  href: string;
  sub?: string;
}) {
  const toneMap: Record<string, string> = {
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
    sky: "bg-sky-50 text-sky-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <Link to={href} className="group">
      <Card className="border border-gray-200 shadow-sm group-hover:shadow-md group-hover:-translate-y-0.5 transition-all">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              {label}
            </p>
            <div
              className={cn(
                "w-9 h-9 rounded-xl flex items-center justify-center",
                toneMap[tone],
              )}
            >
              {icon}
            </div>
          </div>
          <p className="text-3xl font-extrabold text-gray-900 leading-tight">
            {value}
          </p>
          {sub && (
            <p className="text-xs text-gray-500 mt-1 truncate" title={sub}>
              {sub}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function AlertCard({
  tone,
  icon,
  title,
  body,
  actionLabel,
  actionHref,
}: {
  tone: "amber" | "emerald" | "blue" | "gray";
  icon: React.ReactNode;
  title: string;
  body: string;
  actionLabel: string;
  actionHref: string;
}) {
  const toneMap: Record<string, { bg: string; border: string; text: string; iconBg: string; iconColor: string }> = {
    amber: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-900",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
    },
    emerald: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      text: "text-emerald-900",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
    },
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-900",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
    },
    gray: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-900",
      iconBg: "bg-gray-100",
      iconColor: "text-gray-500",
    },
  };
  const c = toneMap[tone];
  return (
    <div
      className={cn(
        "rounded-xl border p-4 flex items-start gap-3",
        c.bg,
        c.border,
      )}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
          c.iconBg,
        )}
      >
        <span className={c.iconColor}>{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn("font-bold text-sm", c.text)}>{title}</p>
        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{body}</p>
      </div>
      <Link
        to={actionHref}
        className={cn(
          "text-xs font-bold whitespace-nowrap flex items-center gap-1 hover:underline shrink-0",
          c.text,
        )}
      >
        {actionLabel}
        <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function QuickTile({
  href,
  icon,
  label,
  tone,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  tone: string;
}) {
  // Tailwind classes need to be fully-qualified strings — no interpolation.
  const toneMap: Record<string, string> = {
    rose: "bg-rose-50 text-rose-700 group-hover:bg-rose-100",
    red: "bg-red-50 text-red-700 group-hover:bg-red-100",
    emerald: "bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100",
    blue: "bg-blue-50 text-blue-700 group-hover:bg-blue-100",
    indigo: "bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100",
    cyan: "bg-cyan-50 text-cyan-700 group-hover:bg-cyan-100",
    amber: "bg-amber-50 text-amber-700 group-hover:bg-amber-100",
    violet: "bg-violet-50 text-violet-700 group-hover:bg-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 group-hover:bg-yellow-100",
    orange: "bg-orange-50 text-orange-700 group-hover:bg-orange-100",
    pink: "bg-pink-50 text-pink-700 group-hover:bg-pink-100",
    slate: "bg-slate-50 text-slate-700 group-hover:bg-slate-100",
    sky: "bg-sky-50 text-sky-700 group-hover:bg-sky-100",
    teal: "bg-teal-50 text-teal-700 group-hover:bg-teal-100",
    fuchsia: "bg-fuchsia-50 text-fuchsia-700 group-hover:bg-fuchsia-100",
    lime: "bg-lime-50 text-lime-700 group-hover:bg-lime-100",
  };
  return (
    <Link to={href} className="group">
      <div
        className={cn(
          "rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-3 shadow-sm group-hover:shadow-md group-hover:-translate-y-0.5 transition-all",
        )}
      >
        <div
          className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
            toneMap[tone] ?? toneMap.blue,
          )}
        >
          {icon}
        </div>
        <span className="font-semibold text-sm text-gray-900 truncate">
          {label}
        </span>
      </div>
    </Link>
  );
}

function StatBlock({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
        {label}
      </p>
      <div className="flex items-center gap-1 mt-1">
        <Flame className="h-3 w-3 text-orange-500" />
        <span className="font-bold text-gray-900">{value}</span>
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  sub,
  icon,
  tone,
  href,
  badge,
}: {
  label: string;
  value: number;
  sub?: string;
  icon: React.ReactNode;
  tone: string;
  href: string;
  badge?: string;
}) {
  const toneMap: Record<string, string> = {
    rose: "bg-rose-50 text-rose-700",
    red: "bg-red-50 text-red-700",
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    indigo: "bg-indigo-50 text-indigo-700",
    cyan: "bg-cyan-50 text-cyan-700",
    amber: "bg-amber-50 text-amber-700",
    teal: "bg-teal-50 text-teal-700",
  };
  const badgeTone =
    tone === "red" ? "bg-red-600 text-white" : "bg-gray-900 text-white";
  return (
    <Link to={href} className="group">
      <div className="relative rounded-xl border border-gray-200 bg-white p-3 shadow-sm group-hover:shadow-md group-hover:-translate-y-0.5 transition-all">
        {badge && (
          <span
            className={cn(
              "absolute top-2 right-2 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full tracking-wide",
              badgeTone,
            )}
          >
            {badge}
          </span>
        )}
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              toneMap[tone] ?? toneMap.blue,
            )}
          >
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide truncate">
              {label}
            </p>
            <p className="text-xl font-extrabold text-gray-900 leading-tight">
              {value.toLocaleString()}
            </p>
          </div>
        </div>
        {sub && (
          <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1">
            <Dot className="h-3 w-3" /> {sub}
          </p>
        )}
      </div>
    </Link>
  );
}
